import React, { useState, useMemo } from "react";
import { Layout, Typography, Card, Row, Col, Button, Tabs, Input, Select, Pagination } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FaEye } from "react-icons/fa";

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const internships = {
  "Software Development": [
    { title: "Frontend Developer", description: "Build responsive web applications using React and modern CSS frameworks.", location: "Nairobi, Kenya", employer: "TechTrend Innovations" },
    { title: "Backend Developer", description: "Develop and maintain server-side logic with Node.js and databases.", location: "Lagos, Nigeria", employer: "CyberPulse Ltd" },
    { title: "Full Stack Developer", description: "Work on both frontend and backend development for scalable apps.", location: "Cape Town, South Africa", employer: "InnoSoft Solutions" },
  ],
  "AI": [
    { title: "AI Engineer", description: "Develop AI models for predictive analytics and automation.", location: "Accra, Ghana", employer: "AI Nexus" },
    { title: "AI Research Scientist", description: "Conduct cutting-edge AI research for innovative solutions.", location: "Cairo, Egypt", employer: "FutureTech Labs" },
  ],
  "Data Science": [
    { title: "Data Scientist", description: "Analyze data to uncover insights and drive business strategies.", location: "Kampala, Uganda", employer: "DataCore Analytics" },
    { title: "Big Data Engineer", description: "Design and optimize big data systems with Hadoop and Spark.", location: "Dar es Salaam, Tanzania", employer: "BigData Innovations" },
  ],
  "Cybersecurity": [
    { title: "Cybersecurity Specialist", description: "Protect systems from cyber threats with advanced security tools.", location: "Addis Ababa, Ethiopia", employer: "SecureNet Africa" },
    { title: "Network Security Engineer", description: "Ensure network integrity and defend against intrusions.", location: "Johannesburg, South Africa", employer: "NetGuard Technologies" },
  ],
};

const pageSize = 8;

const InternshipOpportunities = () => {
  const [activeTab, setActiveTab] = useState("Software Development");
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const allInternships = useMemo(() => {
    return Object.entries(internships).flatMap(([category, jobs]) =>
      jobs.map(job => ({ ...job, category }))
    );
  }, []);

  const countries = [...new Set(allInternships.map((job) => job.location.split(", ")[1]))];

  const filteredInternships = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return allInternships.filter(
      (job) =>
        (activeTab === "All" || job.category === activeTab) &&
        (job.title.toLowerCase().includes(query) || job.description.toLowerCase().includes(query)) &&
        (!countryFilter || job.location.includes(countryFilter))
    );
  }, [activeTab, searchQuery, countryFilter, allInternships]);

  const paginatedInternships = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredInternships.slice(startIndex, startIndex + pageSize);
  }, [filteredInternships, currentPage]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
    setSearchQuery("");
    setCountryFilter(null);
  };

  return (
    <Layout>
      <Content style={{ padding: "60px", background: "linear-gradient(135deg, #e6fffb, #fff7e6)", minHeight: "100vh" }}>
        <Title level={2} style={{ fontSize: "20px", color: "#13c2c2", textAlign: "center", fontWeight: "700", marginBottom: "28px" }}>
          <FaEye style={{ marginRight: "14px", fontSize: "20px", color: "#fa8c16" }} /> Internship Opportunities
        </Title>
        <Paragraph style={{ fontSize: "16px", color: "#595959", textAlign: "center", marginBottom: "32px", maxWidth: "800px", marginLeft: "auto", marginRight: "auto", lineHeight: "1.6" }}>
          Kickstart your tech career with exciting internships in AI, Cybersecurity, Data Science, and Software Development. Join JectHub and turn your passion into action!
        </Paragraph>

        <Row gutter={[24, 24]} style={{ marginBottom: "32px", alignItems: "center" }}>
          <Col xs={24} md={12}>
            <Tabs
              defaultActiveKey="Software Development"
              onChange={handleTabChange}
              tabBarStyle={{ fontWeight: "700", color: "#13c2c2", borderBottom: "3px solid #13c2c2" }}
            >
              <TabPane tab="All" key="All" />
              {Object.keys(internships).map((category) => (
                <TabPane tab={category} key={category} />
              ))}
            </Tabs>
          </Col>
          <Col xs={24} md={6}>
            <Input
              placeholder="Search internships by title or description"
              prefix={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{ borderRadius: "8px", height: "44px", boxShadow: "0 3px 6px rgba(0,0,0,0.1)", border: "none" }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Filter by country"
              size="large"
              onChange={(value) => {
                setCountryFilter(value);
                setCurrentPage(1);
              }}
              style={{ width: "100%", borderRadius: "8px" }}
              allowClear
              dropdownStyle={{ borderRadius: "8px" }}
            >
              {countries.map((country) => (
                <Option key={country} value={country}>
                  {country}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          {paginatedInternships.length > 0 ? (
            paginatedInternships.map((job, index) => (
              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <div className="flip-container" style={{ perspective: "1000px", height: "360px" }}>
                  <div className="flip-card">
                    {/* Front Side */}
                    <Card
                      className="flip-card-front"
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        borderRadius: "16px",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                        background: "linear-gradient(135deg, #ffffff, #f0fafa)",
                        padding: "20px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <div>
                        <Title level={4} style={{ color: "#1a1a1a", marginBottom: "12px", fontSize: "20px", fontWeight: "600" }}>
                          {job.title}
                        </Title>
                        <Paragraph style={{ color: "#595959", fontSize: "14px", marginBottom: "12px" }}>
                          <strong style={{ color: "#13c2c2" }}>Employer:</strong> {job.employer}
                        </Paragraph>
                        <Paragraph style={{ color: "#595959", fontSize: "14px" }}>
                          <strong style={{ color: "#13c2c2" }}>Location:</strong> {job.location}
                        </Paragraph>
                      </div>
                      <Button
                        type="primary"
                        href="/signin"
                        icon={<FaEye style={{ marginRight: "8px" }} />}
                        style={{
                          background: "linear-gradient(45deg, #fa8c16, #d46b08)",
                          border: "none",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          height: "44px",
                          width: "100%",
                          marginTop: "20px",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "linear-gradient(45deg, #d46b08, #ad4e00)";
                          e.target.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "linear-gradient(45deg, #fa8c16, #d46b08)";
                          e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                        }}
                      >
                        View Details
                      </Button>
                    </Card>
                    {/* Back Side */}
                    <Card
                      className="flip-card-back"
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        borderRadius: "16px",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                        background: "linear-gradient(135deg, #f0fafa, #e6fffb)",
                        padding: "20px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <Title level={4} style={{ color: "#1a1a1a", marginBottom: "12px", fontSize: "20px", fontWeight: "600" }}>
                          {job.title}
                        </Title>
                        <Paragraph style={{ color: "#595959", fontSize: "14px", height: "180px", overflow: "auto", lineHeight: "1.5" }}>
                          {job.description}
                        </Paragraph>
                      </div>
                      <Button
                        type="primary"
                        href="/signin"
                        icon={<FaEye style={{ marginRight: "8px" }} />}
                        style={{
                          background: "linear-gradient(45deg, #13c2c2, #08979c)",
                          border: "none",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          height: "44px",
                          width: "100%",
                          marginTop: "20px",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "linear-gradient(45deg, #08979c, #006d75)";
                          e.target.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "linear-gradient(45deg, #13c2c2, #08979c)";
                          e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                        }}
                      >
                        View Details
                      </Button>
                    </Card>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: "center" }}>
              <Paragraph style={{ color: "#999", fontSize: "16px", marginTop: "24px" }}>
                No internships match your search or filters. Try adjusting your criteria!
              </Paragraph>
            </Col>
          )}
        </Row>

        <Pagination
          current={currentPage}
          total={filteredInternships.length}
          pageSize={pageSize}
          onChange={setCurrentPage}
          style={{ marginTop: "32px", textAlign: "center", fontSize: "16px" }}
          showSizeChanger={false}
        />

        <Button
          type="primary"
          href="/signin"
          style={{
            marginTop: "32px",
            background: "linear-gradient(45deg, #52c41a, #389e0d)",
            border: "none",
            borderRadius: "8px",
            fontWeight: "bold",
            height: "48px",
            padding: "0 24px",
            fontSize: "16px",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "linear-gradient(45deg, #389e0d, #237804)";
            e.target.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "linear-gradient(45deg, #52c41a, #389e0d)";
            e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
          }}
        >
          Add an Internship (Employers)
        </Button>

        {/* Custom CSS */}
        <style jsx global>{`
          .flip-container {
            perspective: 1000px;
          }
          .flip-card {
            position: relative;
            width: 100%;
            height: 100%;
            transition: transform 0.6s;
            transform-style: preserve-3d;
          }
          .flip-container:hover .flip-card {
            transform: rotateY(180deg);
          }
          .flip-card-front,
          .flip-card-back {
            position: absolute;
            width: 100%;
            height: 100%;
            backface-visibility: hidden;
            animation: fadeIn 0.5s ease-in-out;
          }
          .flip-card-back {
            transform: rotateY(180deg);
          }
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #13c2c2 !important;
          }
          .ant-tabs-ink-bar {
            background: #13c2c2 !important;
          }
          .ant-pagination-item-active {
            border-color: #fa8c16 !important;
          }
          .ant-pagination-item-active a {
            color: #fa8c16 !important;
          }
          .ant-select-selector {
            border: none !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.1) !important;
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-select-selection-placeholder {
            color: #888 !important;
          }
          @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default InternshipOpportunities;